import React, { useId } from 'react';
import { Table, Empty } from 'antd';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';

// Reserved for the pagination bar (control row height + margin) when pagination is enabled,
// so it lands right at the bottom of `height` instead of riding up under a short result set.
const PAGINATION_RESERVED_HEIGHT = 56;

// ==============================|| TABLE CONTAINER ||============================== //

/**
 * Standard reusable Ant Design Table wrapper — bakes in every convention worked out on
 * Batch Traceability / Batch Process Details / PR List so new tables don't have to
 * reinvent it:
 *  - A fixed-height rows box (min-height === scroll.y, never a min/max range — mixing
 *    the two makes the body fight itself once real rows appear)
 *  - Optional pagination reserved a fixed strip below that box, so the pagination bar is
 *    always visible right at `height`'s boundary, whether there are 0, 2, or 50 rows
 *  - A thin, visible, theme-colored scrollbar in both directions, covering whichever DOM
 *    structure Ant renders (.ant-table-body when scroll.y is set, .ant-table-content when
 *    it isn't)
 *  - A themed header, subtle row hover, and a centered empty-state placeholder
 *  - Full dark-mode awareness
 *
 * `height` accepts either a number (px) or any valid CSS length, including a
 * viewport-anchored `calc(100vh - Npx)` string — pass whichever fits the page's layout.
 * Pass `height={null}` explicitly to opt OUT of the fixed-height box entirely — no
 * scroll.y, no min-height floor, the table just grows with its content like a plain
 * <Table>. Use this for tables that never had a height cap in the original code.
 *
 * `minHeight` is independent of `height` — it sets a floor (via .ant-table-container,
 * which exists regardless of scroll config) without capping growth or adding scroll.
 * Use it alongside `height={null}` when a table should never look collapsed with just
 * a couple of rows, but also should never clip/scroll if it grows past that floor.
 */
const SKELETON_ROW_COUNT = 6;

const TableContainer = ({
  columns,
  data,
  rowKey = 'key',
  expandable,
  height = 450,
  minHeight,
  pagination = false,
  // Neutral by default (matches Ant's own <Table> defaults exactly) — bordered/size/scrollX
  // change real layout (row height, horizontal scroll structure), not just paint, so this
  // component never silently imposes them. Pass them explicitly per page to opt in.
  bordered = false,
  size,
  scrollX,
  rowClassName,
  locale,
  className = '',
  onRow,
  loading,
  ...rest
}) => {
  const { isDark } = useTheme();
  const scopeClass = `tc-${useId().replace(/[:]/g, '')}`;

  // With no rows, the header still renders at its full scrollX width, which would
  // force a horizontal scrollbar just to see the header — with nothing below it to
  // actually scroll to. Suppress scrollX when empty so an empty table never shows a
  // scrollbar in either direction.
  const isEmpty = !data || data.length === 0;
  const effectiveScrollX = isEmpty ? undefined : scrollX;

  // First load (no rows yet): swap real cells for pulsing skeleton bars instead of
  // Ant's plain spinner, using the SAME columns (so widths/alignment don't jump once
  // real data arrives). A later refetch with rows already on screen keeps Ant's own
  // overlay spinner instead — replacing visible data with skeleton bars on every
  // refresh would be more distracting than helpful.
  const isInitialLoading = !!loading && isEmpty;
  const skeletonBarColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const skeletonColumns = isInitialLoading
    ? columns?.map((col) => ({
        ...col,
        sorter: undefined,
        filters: undefined,
        onFilter: undefined,
        render: () => (
          <div
            style={{
              height: 14,
              borderRadius: 4,
              background: skeletonBarColor,
              animation: 'tc-skeleton-pulse 1.4s ease-in-out infinite',
            }}
          />
        ),
      }))
    : columns;
  const skeletonData = isInitialLoading
    ? Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => ({ __tcSkeletonKey: i }))
    : data;

  const noHeightCap = height === null;
  const hasPagination = !!pagination;
  const rowsBoxHeight = noHeightCap
    ? undefined
    : !hasPagination
      ? height
      : typeof height === 'number'
        ? height - PAGINATION_RESERVED_HEIGHT
        : `calc(${height} - ${PAGINATION_RESERVED_HEIGHT}px)`;

  const paginationConfig = !hasPagination
    ? false
    : {
        pageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ['10', '15', '20', '50', '100'],
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        ...(pagination === true ? {} : pagination),
      };

  const headerBg = isDark ? defaultColors.palette.gray[700] : '#fafafa';
  const headerText = isDark ? '#f9fafb' : '#1f2937';
  const rowHoverBg = isDark ? defaultColors.palette.gray[700] : '#f8fafc';
  const border = isDark ? defaultColors.palette.gray[700] : '#e5e7eb';
  const scrollbarColor = isDark ? '#64748b' : '#94a3b8';
  const scrollbarHoverColor = isDark ? '#94a3b8' : '#64748b';
  const rowsBoxHeightCss = typeof rowsBoxHeight === 'number' ? `${rowsBoxHeight}px` : rowsBoxHeight;
  const minHeightCss = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;

  return (
    <>
      <Table
        className={`${scopeClass} ${className}`.trim()}
        size={size}
        bordered={bordered}
        pagination={isInitialLoading ? false : paginationConfig}
        columns={skeletonColumns}
        dataSource={skeletonData}
        rowKey={isInitialLoading ? '__tcSkeletonKey' : rowKey}
        expandable={isInitialLoading ? undefined : expandable}
        rowClassName={isInitialLoading ? undefined : rowClassName}
        onRow={isInitialLoading ? undefined : onRow}
        loading={isInitialLoading ? false : loading}
        scroll={effectiveScrollX || rowsBoxHeight ? { x: effectiveScrollX, y: rowsBoxHeight } : undefined}
        locale={{ emptyText: <Empty description="No data" />, ...locale }}
        {...rest}
      />
      <style>{`
        ${minHeightCss != null ? `
        .${scopeClass} .ant-table-container {
          min-height: ${minHeightCss};
        }` : ''}
        /* Hard, unconditional guard: with zero rows there is never anything to actually
           scroll to, in either direction, regardless of height mode — so force every
           scrolling element Ant might have created (.ant-table-body when scroll.y is
           set, .ant-table-content otherwise, plus the outer container as a backstop)
           to hide overflow rather than relying on precisely matching the placeholder's
           height to the box (fragile — depends on <Empty>'s own internal sizing, which
           can render a pixel or two taller than expected and trip a scrollbar anyway). */
        ${isEmpty ? `
        .${scopeClass} .ant-table-body,
        .${scopeClass} .ant-table-content,
        .${scopeClass} .ant-table-container {
          overflow: hidden !important;
        }` : ''}
        .${scopeClass} .ant-table-thead > tr > th {
          background: ${headerBg} !important;
          color: ${headerText} !important;
          font-weight: 600 !important;
        }
        /* No forced row background here (deliberately) — several pages use rowClassName
           to convey real meaning (batch-type coloring, child-row nesting, etc.); a forced
           !important background on every td would silently erase that. Hover is safe
           (cosmetic only). The border-bottom rule only applies when NOT using Ant's own
           bordered mode — bordered's own full grid already has a consistent color on
           all four sides; adding our own bottom-only override on top of it just paints
           one edge a different shade than the other three. */
        ${!bordered ? `
        .${scopeClass} .ant-table-tbody > tr > td {
          border-bottom: 1px solid ${border} !important;
        }` : ''}
        .${scopeClass} .ant-table-tbody > tr:hover > td {
          background: ${rowHoverBg};
        }
        ${!noHeightCap ? `
        .${scopeClass} .ant-table-body {
          min-height: ${rowsBoxHeightCss} !important;
        }
        /* bordered's own box border only wraps the table's NATURAL content height —
           once min-height stretches .ant-table-body taller than that (e.g. one row in
           a 400px box), the right/bottom edges of that border stop short of the reserved
           empty space instead of enclosing it. Draw them directly on the stretched
           element itself so the box reads as fully bordered at any row count. */
        ${bordered ? `
        .${scopeClass} .ant-table-body {
          border-right: 1px solid ${border};
          border-bottom: 1px solid ${border};
        }` : ''}
        .${scopeClass} .ant-table-placeholder {
          height: ${rowsBoxHeightCss};
          max-height: ${rowsBoxHeightCss};
          overflow: hidden;
        }
        .${scopeClass} .ant-table-placeholder > td {
          height: ${rowsBoxHeightCss} !important;
          max-height: ${rowsBoxHeightCss} !important;
          overflow: hidden;
          vertical-align: middle;
          border-bottom: none !important;
        }` : ''}
        /* height={null} skips the block above entirely, so when minHeight is also set
           (stretching .ant-table-container) the empty-state placeholder needs its own
           clip here — otherwise Ant's <Empty> icon/text can render fractionally taller
           than its natural box on some viewports, tripping a scrollbar for a table that
           has no data to actually scroll. */
        ${noHeightCap && minHeightCss != null ? `
        .${scopeClass} .ant-table-placeholder,
        .${scopeClass} .ant-table-placeholder > td {
          overflow: hidden;
        }` : ''}
        .${scopeClass} .ant-table-body,
        .${scopeClass} .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: ${scrollbarColor} transparent;
        }
        .${scopeClass} .ant-table-body::-webkit-scrollbar,
        .${scopeClass} .ant-table-content::-webkit-scrollbar {
          height: 6px;
          width: 6px;
        }
        .${scopeClass} .ant-table-body::-webkit-scrollbar-track,
        .${scopeClass} .ant-table-content::-webkit-scrollbar-track {
          background: transparent;
        }
        .${scopeClass} .ant-table-body::-webkit-scrollbar-thumb,
        .${scopeClass} .ant-table-content::-webkit-scrollbar-thumb {
          background: ${scrollbarColor};
          border-radius: 4px;
        }
        .${scopeClass} .ant-table-body::-webkit-scrollbar-thumb:hover,
        .${scopeClass} .ant-table-content::-webkit-scrollbar-thumb:hover {
          background: ${scrollbarHoverColor};
        }
        @keyframes tc-skeleton-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
};

export default TableContainer;
